import { HttpErrorResponse, HttpEvent, HttpEventType } from '@angular/common/http';
import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { ConfigService } from 'src/app/services/config/config.service';
import { IngestUploadResult } from 'src/app/messaging/ingestFile';
import { INGEST_FILE_ACCEPT, getIngestFileTypeForName } from 'src/app/objectModel/ingestFileType';
import { formatBytes } from 'src/app/base/formatBytes';

// 'Warning' is for a non-fatal rejection the Analyst can act on (today: the
// server's 409 "a file with this name already exists" conflict) - distinct
// from 'Failed' (a genuine error) so the UI can color them differently
// (orange vs red) per the Architect's request.
type UploadStatus = 'Uploading' | 'Success' | 'Warning' | 'Failed';

interface UploadRow {
  name: string;
  sizeBytes: number;
  typeLabel: string;
  progress: number;
  status: UploadStatus;
  errorMessage?: string;
}

/**
 * Browse/drag-drop + per-file upload progress. Each selected file is its own
 * HTTP request and its own row - a failure only ever marks that one row
 * Failed, it never blocks or hides the others. Emits (uploaded) once every
 * file dispatched to the server in the current batch has settled
 * (succeeded or failed), so the host can refresh the tree exactly once per
 * batch rather than once per file.
 */
@Component({
    selector: 'app-ingest-file-upload',
    templateUrl: './ingest-file-upload.component.html',
    styleUrls: ['./ingest-file-upload.component.css'],
    standalone: false
})
export class IngestFileUploadComponent implements OnInit {

  @Output() uploaded = new EventEmitter<void>();

  rows: UploadRow[] = [];
  acceptTypes = INGEST_FILE_ACCEPT;
  formatBytes = formatBytes;
  // Populated from the server so this always reflects the real configured
  // IngestUploadMaxSizeMB rather than a hard-coded guess that could drift
  // from it. Undefined until loaded - the hint/check are skipped until then
  // rather than showing a wrong number.
  maxUploadSizeBytes: number;

  private pendingCount = 0;

  constructor(private configService: ConfigService) { }

  async ngOnInit(): Promise<void> {
    try {
      const limits = await this.configService.getIngestUploadLimitsAsync();
      this.maxUploadSizeBytes = limits.maxUploadSizeMB * 1024 * 1024;
    } catch {
      // Non-fatal - the server still enforces the real limit either way;
      // this only powers the hint text and the client-side fail-fast check.
    }
  }

  fileBrowseHandler(fileList: FileList): void {
    this.handleSelectedFiles(fileList);
  }

  onFileDropped(fileList: FileList): void {
    this.handleSelectedFiles(fileList);
  }

  removeRow(row: UploadRow): void {
    const idx = this.rows.indexOf(row);
    if (idx >= 0) { this.rows.splice(idx, 1); }
  }

  private handleSelectedFiles(fileList: FileList): void {
    Array.from(fileList ?? []).forEach(file => this.startUpload(file));
  }

  private startUpload(file: File): void {
    const row: UploadRow = {
      name: file.name,
      sizeBytes: file.size,
      typeLabel: getIngestFileTypeForName(file.name)?.type ?? 'Unsupported',
      progress: 0,
      status: 'Uploading'
    };
    this.rows.unshift(row);

    // Fail fast client-side for an unsupported extension or an oversized
    // file - neither is ever dispatched to the server, and neither counts
    // toward the batch's settle count. The server still enforces both
    // independently regardless of this check.
    if (!getIngestFileTypeForName(file.name)) {
      row.status = 'Failed';
      row.errorMessage = `Unsupported file type. Supported types: ${this.acceptTypes}`;
      return;
    }
    if (this.maxUploadSizeBytes && file.size > this.maxUploadSizeBytes) {
      row.status = 'Failed';
      row.errorMessage = `File exceeds the maximum size of ${formatBytes(this.maxUploadSizeBytes)}.`;
      return;
    }

    this.pendingCount++;
    this.configService.uploadIngestFile(file).subscribe({
      next: (event: HttpEvent<IngestUploadResult>) => {
        if (event.type === HttpEventType.UploadProgress && event.total) {
          row.progress = Math.round((100 * event.loaded) / event.total);
        } else if (event.type === HttpEventType.Response) {
          const result = event.body;
          if (result?.success) {
            row.status = 'Success';
            row.progress = 100;
          } else {
            row.status = 'Failed';
            row.errorMessage = result?.message ?? 'Upload failed.';
          }
          this.onUploadSettled();
        }
      },
      error: (err: HttpErrorResponse) => {
        // 409 = IngestFileConflictException ("already exists") - a warning
        // the Analyst can act on (rename/delete the existing file), not a
        // hard error. Every other status is a genuine failure.
        row.status = err.status === 409 ? 'Warning' : 'Failed';
        row.errorMessage = err.error?.message ?? 'Upload failed.';
        this.onUploadSettled();
      }
    });
  }

  private onUploadSettled(): void {
    this.pendingCount = Math.max(0, this.pendingCount - 1);
    if (this.pendingCount === 0) {
      this.uploaded.emit();
    }
  }
}
